import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import styled, { css } from 'styled-components';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';
import theme from 'theme/theme';
import MainTitle from '../MainTitle/MainTitle';
import { FileIcon, FileUploadIcon, CloseIcon } from '../Icon';
import Flex from '../Flex/Flex';
import LargeLabel from '../LargeLabel';
import FieldError from '../forms/FieldError';
import { usePrevious } from '../../../utils';

const DropZoneContainer = styled(Flex)`
  position: relative;
  align-content: center;
  justify-content: center;
  min-height: 150px;
  padding: 30px;
  border: 0;
  outline-offset: -3px;
  text-align: center;
  background: ${theme.colors.light};
  color: ${theme.colors.dark};

  &:hover {
    cursor: pointer;
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    ${MainTitle} {
      color: #fff;
    }
  }

  ${(props) => (props.isDragActive) && css`
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    ${MainTitle} {
      color: #fff;
    }
  `}

  ${(props) => props.hasError && css`
    &, &:focus {
      outline: 3px solid ${theme.colors.primary};
    }
  `}
`;
const DropInfo = styled.div`
`;

const FileWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  color: ${theme.colors.light};
  background: ${theme.colors.darker};
  border-top: 1px solid ${theme.colors.dark};
  text-align: left;
`;


const FileName = styled(LargeLabel)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${theme.colors.light};
`;

export const FileIconCol = styled(Flex)`
  background: ${theme.colors.darker};
  min-width: 50px;
  max-width: 50px;
  align-items: center;
  justify-content: center;
`;

const FileMainCol = styled.div`
  display: flex;
  margin: 10px 20px;
  flex-direction: column;
  justify-content: center;
`;

const FileActionCol = styled(Flex)`
  margin: 12px 0 12px 12px;
  flex-grow: 0;
  margin-left: auto;
  min-width: 50px;
`;

const FileAction = styled(Flex)`
  border-left: 1px solid ${theme.colors.dark};
  flex-grow: 0;
  align-items: center;
  justify-content: center;
  padding: 10px;
  &:hover svg {
    fill: ${theme.colors.primary}
  }
`;


const File = ({
  name, onRemove, folderPath,
}) => (
  <FileWrapper>

    <FileIconCol>
      <FileIcon />
    </FileIconCol>

    <FileMainCol>
      {folderPath && (<div>{folderPath}</div>)}
      <FileName>{name}</FileName>
    </FileMainCol>

    <FileActionCol className="action-col">
      <FileAction>
        <CloseIcon size="24px" onClick={onRemove} />
      </FileAction>
    </FileActionCol>

  </FileWrapper>
);
File.propTypes = {
  name: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  folderPath: PropTypes.string,
};
File.defaultProps = {
  folderPath: null,
};

async function fileEventHandler(event) {
  return getDroppedOrSelectedFiles(event)
    .then((files) => files.map((file) => {
      const newFile = file;
      // Remove prefix '/', which is added even to simple files when using dnd uplaod,
      // but not when selecting files in the dialog
      if (file.fullPath && file.fullPath[0] === '/') {
        newFile.fullPath = file.fullPath.substr(1);
      }
      newFile.hasFolder = newFile.fullPath.includes('/');
      if (newFile.hasFolder) {
        newFile.folderPath = newFile.fullPath
          .substring(0, newFile.fullPath.lastIndexOf('/'));
      } else {
        newFile.folderPath = '';
      }
      return newFile;
    }));
}

function BaseDropzone({
  multiple, allowedExtensions, onDrop, onFilesChange, accept, ...otherProps
}) {
  const [hasError] = useState(otherProps.hasError);

  const [files, setFiles] = useState([]);
  const prevFiles = usePrevious(files);
  useEffect(() => {
    if (prevFiles !== files) {
      onFilesChange(files);
    }
  }, [files, onFilesChange, prevFiles]);

  const onDropHandler = useCallback((acceptedFiles) => {
    setFiles([...files, ...acceptedFiles]);
    onDrop(acceptedFiles);
  }, [files, onDrop]);

  const handleRemove = (event, key) => {
    event.stopPropagation(); // we do not want the file add dialog to show up
    setFiles(files.filter((file, i) => i !== key));
  };

  const {
    getRootProps, getInputProps, isDragActive, isDragReject, isDragAccept,
  } = useDropzone({
    onDrop: onDropHandler,
    multiple,
    accept,
    getFilesFromEvent: (event) => fileEventHandler(event),
  });

  const hasFiles = (files.length > 0);
  const hasFolders = !!files.find((file) => file.hasFolder);
  const filePreviews = files && files
    .sort((a, b) => a.fullPath.toLowerCase().localeCompare(b.fullPath.toLowerCase()))
    .map((file, key) => (
      <File
        key={key /* eslint-disable-line */}
        name={file.name}
        hasFolders={hasFolders}
        folderPath={file.folderPath}
        onRemove={(e) => handleRemove(e, key)}
      />
    ));

  return (
    <>
      <DropZoneContainer
        isDragActive={isDragActive}
        isDragReject={isDragReject}
        isDragAccept={isDragAccept}
        hasFiles={hasFiles}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        hasError={hasError}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <DropInfo>
          <FileUploadIcon size="2em" />
          <MainTitle>
            Drop
            {multiple ? ' files ' : ' file ' }
            here to upload
          </MainTitle>
            or click
        </DropInfo>
      </DropZoneContainer>

      {hasError && allowedExtensions !== '*' && (
        <FieldError>
              Only the following types are allowed:
          {' '}
          <br />
          {allowedExtensions.map((e) => `${e}, `)}
        </FieldError>
      )}

      {hasFiles && filePreviews}

    </>
  );
}

BaseDropzone.propTypes = {
  onDrop: PropTypes.func,
  onFilesChange: PropTypes.func,
  hasError: PropTypes.bool,
  multiple: PropTypes.bool,
  accept: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
};
BaseDropzone.defaultProps = {
  onDrop: () => {},
  onFilesChange: () => {},
  hasError: false,
  multiple: true,
  accept: [
    '.txt',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.xlsm',
    '.msg',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.tiff',
    '.ppt',
    '.pptx',
    '.fzp',
    '.potx',
    '.fzd',
    '.ozd',
    '.zip',
    '.gzip',
    '.mp4',
    '.mp3',
  ],
};

export default BaseDropzone;
